// The open slot the user picked from the results; the day preview only exists while one is picked.

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { DayResults, SlotOption } from './ehrAppointmentV2.types'
import { slotKey } from './pairing'

type Args = {
  days: DayResults[]
  // False while the list is loading, so an empty in-between list does not drop the pick.
  isSettled: boolean
  // Changes on every search run.
  searchId: number
}

export const findSlot = (days: readonly DayResults[], key: string | null): SlotOption | undefined =>
  key === null
    ? undefined
    : days.flatMap((day) => day.options).find((slot) => slotKey(slot) === key)

export const useSelectedSlot = ({ days, isSettled, searchId }: Args) => {
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  const selectedSlot = useMemo(() => findSlot(days, pickedKey), [days, pickedKey])

  // A new search starts with nothing picked.
  useEffect(() => setPickedKey(null), [searchId])

  // A pick the settled list no longer offers is dropped, not revived later.
  useEffect(() => {
    if (isSettled && pickedKey !== null && !selectedSlot) setPickedKey(null)
  }, [isSettled, pickedKey, selectedSlot])

  const select = useCallback((slot: SlotOption) => setPickedKey(slotKey(slot)), [])
  const clear = useCallback(() => setPickedKey(null), [])

  return {
    selectedKey: selectedSlot ? pickedKey : null,
    selectedSlot,
    select,
    clear,
  }
}
